import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, MessageCircle, Receipt, MapPin } from "lucide-react";
import api, { formatApiErrorDetail, imageUrl } from "../lib/api";
import { toast } from "sonner";
import WhatsAppPush from "../components/WhatsAppPush";
import { PhoneField, PreferredContactMethodField } from "../components/PhoneField";
import { buildPhoneFieldValue, getPhoneFieldError, touchPhoneField } from "../lib/phone";
import { formatISK } from "../lib/currency";

const WA_NUMBER = "3547883999";
const WA_MSG = encodeURIComponent("Hi 👋\nThanks for your request.\n\nPlease send:\n• Pickup location\n• Drop-off location\n• Date & time\n• Number of passengers\n\nI'll confirm the price immediately 👍");
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${WA_MSG}`;

// Curated Iceland imagery — cinematic, cool tones (all URLs verified to resolve).
const HERO_IMG = "https://customer-assets.emergentagent.com/job_b7222ed5-91f4-4808-a840-bb97b21f35ac/artifacts/tu9sqio5_image.png"; // Aerial glacier road bridge, Iceland
const FEATURED_VAN = "https://customer-assets.emergentagent.com/job_b7222ed5-91f4-4808-a840-bb97b21f35ac/artifacts/dlxxv8vx_image.png"; // Van on scenic Iceland road
const BLACK_VAN = "https://customer-assets.emergentagent.com/job_b7222ed5-91f4-4808-a840-bb97b21f35ac/artifacts/hojxus8e_image.png"; // Airport transfer — van + jet
const WATERFALL = "https://customer-assets.emergentagent.com/job_b7222ed5-91f4-4808-a840-bb97b21f35ac/artifacts/fuw8993x_image.png"; // Iceland tour landscape

export default function Home() {
  const [vehicles, setVehicles] = React.useState([]);
  React.useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden grain bg-slate-950 text-white">
        <img
          src={HERO_IMG}
          alt="Aerial view of a glacier river braid in Iceland"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
          style={{ filter: "contrast(1.20) saturate(1.05) brightness(0.90)" }}
        />
        {/* Left-side dark gradient overlay (40% → 0%) for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/15 to-transparent" />
        {/* Slight overall base darkening to deepen shadows without warming tone */}
        <div className="absolute inset-0 bg-slate-950/10" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 lg:py-40">
          <span
            data-testid="hero-eyebrow"
            className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-sky-300 reveal"
          >
            Iceland · Reykjavík
          </span>
          <h1
            data-testid="hero-heading"
            className="mt-4 font-heading text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tighter max-w-3xl reveal reveal-d1"
          >
            Private Airport Transfer in Iceland
          </h1>
          <p
            data-testid="hero-subheading"
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-200 reveal reveal-d2"
          >
            Fixed pricing · VAT included · Fast booking.
          </p>
          <p
            data-testid="hero-price-anchor"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 px-3.5 py-1.5 text-sm font-semibold text-white reveal reveal-d2"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
            KEF → Reykjavík from <span className="tabular-nums">{formatISK(17990)}</span>
          </p>
          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3 reveal reveal-d3">
            <Link
              to="/transfers"
              data-testid="hero-primary-cta"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md bg-white px-6 py-3.5 text-sm font-semibold text-slate-900 hover:bg-sky-50 transition-colors"
            >
              Book your transfer <ArrowRight size={16} />
            </Link>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-secondary-cta"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md border border-white/40 bg-transparent px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                <path d="M17.5 14.4c-.3-.1-1.7-.9-2-1s-.5-.1-.7.1-.8 1-.9 1.2c-.2.2-.3.2-.6.1-1.8-.9-3-1.6-4.2-3.7-.3-.5.3-.5.9-1.6.1-.2.1-.4 0-.6s-.7-1.7-1-2.3c-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1.1 1.1-1.1 2.7s1.1 3.1 1.3 3.4c.2.2 2.3 3.5 5.5 4.9.8.3 1.4.5 1.9.7.8.2 1.5.2 2.1.1.6-.1 1.7-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.4 1.3 4.8L2 22l5.3-1.4c1.3.7 2.9 1.1 4.7 1.1 5.5 0 10-4.5 10-10S17.5 2 12 2z" />
              </svg>
              Message on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">Services</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">
              Choose how you explore Iceland
            </h2>
          </div>
          <Link to="/contact" className="hidden sm:inline-flex text-sm font-semibold text-slate-900 hover:underline">
            Custom request →
          </Link>
        </div>
        <div className="grid gap-5 md:grid-cols-3" data-testid="services-grid">
          <ServiceCard
            title="9-Seat Van Rental"
            desc="Self-drive Iceland your way. Spacious 9-seat vans, flexible pickup, and unlimited freedom to explore."
            cta="/rentals"
            ctaLabel="Check availability"
            img={FEATURED_VAN}
            testid="service-card-rental"
          />
          <ServiceCard
            title="Private Airport Transfers"
            desc="Door-to-door pickup with a professional driver. Comfortable, reliable, and always on time — no waiting."
            cta="/transfers"
            ctaLabel="Book your transfer"
            img={BLACK_VAN}
            testid="service-card-transfers"
          />
          <ServiceCard
            title="Private Tours in Iceland"
            desc="Custom day tours with your own driver. Waterfalls, glaciers, hidden spots — built around your schedule."
            cta="/tours"
            ctaLabel="Plan your tour"
            img={WATERFALL}
            testid="service-card-tours"
          />
        </div>

        {/* Pricing anchor */}
        <div
          data-testid="pricing-anchor"
          className="mt-10 rounded-xl border border-slate-200 bg-white px-6 py-5 flex flex-wrap items-center justify-between gap-4"
        >
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">Rental pricing</p>
            <p className="mt-1 text-slate-900 font-heading text-lg font-semibold">
              From <span className="tabular-nums">{formatISK(28000)}</span> / day · From <span className="tabular-nums">{formatISK(129000)}</span> / week
            </p>
            <p className="mt-1 text-xs text-slate-500">VAT included. Transfers &amp; tours priced per request.</p>
          </div>
          <Link
            to="/rentals"
            data-testid="pricing-cta"
            className="inline-flex w-full sm:w-auto items-center justify-center gap-1.5 rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Check availability <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid gap-8 md:grid-cols-4" data-testid="trust-section">
          <Trust icon={<MapPin size={18} />} title="Local Iceland operator" desc="Based in Reykjavík." />
          <Trust icon={<Receipt size={18} />} title="VAT included" desc="No surprise fees." />
          <Trust icon={<Check size={18} />} title="Flexible bookings" desc="Short, custom or long-term." />
          <Trust icon={<MessageCircle size={18} />} title="Direct WhatsApp support" desc="Talk to a human, fast." />
        </div>
      </section>

      {/* WhatsApp push */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <WhatsAppPush />
      </section>

      {/* Featured vehicle */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid gap-10 md:grid-cols-2 items-center">
          <div className="order-2 md:order-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">Featured fleet</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">
              Built for groups of up to nine.
            </h2>
            <p className="mt-4 text-slate-600 max-w-lg">
              Our core fleet is the 9-seater VW Caravelle. Self-drive rentals use all 9 seats
              (driver included). Chauffeur-driven tours and transfers seat up to 8 passengers plus a driver.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li className="flex items-center gap-2"><ShieldCheck size={16} className="text-sky-700" /> Fully insured, regularly serviced</li>
              <li className="flex items-center gap-2"><ShieldCheck size={16} className="text-sky-700" /> Child seats and add-ons available</li>
              <li className="flex items-center gap-2"><ShieldCheck size={16} className="text-sky-700" /> Cross-use for rentals, tours and transfers</li>
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/rentals" className="inline-flex w-full sm:w-auto items-center justify-center rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                See availability
              </Link>
              <Link to="/long-term" className="inline-flex w-full sm:w-auto items-center justify-center rounded-md border border-slate-300 bg-transparent px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50">
                Long-term offer
              </Link>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="relative overflow-hidden rounded-xl border border-slate-200 ice-ring">
              <img
                src={vehicles[0]?.images?.[0] ? imageUrl(vehicles[0].images[0]) : FEATURED_VAN}
                alt="VW Caravelle 9-Seater"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-5">
                <p className="text-white font-heading text-lg font-semibold">
                  {vehicles[0]?.name || "VW Caravelle 9-Seater"}
                </p>
                <p className="text-slate-200 text-xs">
                  9 seats incl. driver (self-drive) · 8 + driver (chauffeur) · rental · tours · transport
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick inquiry */}
      <QuickInquiry />
    </div>
  );
}

function ServiceCard({ title, desc, cta, ctaLabel = "Explore", img, testid }) {
  return (
    <Link
      to={cta}
      data-testid={testid}
      className="group relative block overflow-hidden rounded-xl border border-slate-200 bg-white hover:-translate-y-1 transition-transform"
    >
      <div className="aspect-[4/3] overflow-hidden bg-slate-100">
        <img
          src={img}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-6">
        <h3 className="font-heading text-xl font-semibold tracking-tight">{title}</h3>
        <p className="mt-2 text-sm text-slate-600">{desc}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-900">
          {ctaLabel} <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

function Trust({ icon, title, desc }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-sky-700">{icon}<span className="text-xs font-bold uppercase tracking-[0.2em]">{title}</span></div>
      <p className="mt-2 text-slate-700 text-sm">{desc}</p>
    </div>
  );
}

function QuickInquiry() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: buildPhoneFieldValue(),
    preferred_contact_method: null,
    message: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const phoneError = getPhoneFieldError(form.phone);
    if (phoneError) {
      setForm({ ...form, phone: touchPhoneField(form.phone) });
      toast.error(phoneError);
      return;
    }
    setBusy(true);
    try {
      await api.post("/inquiries?kind=contact", {
        name: form.name,
        email: form.email,
        phone: form.phone.e164 || null,
        phone_country: form.phone.country || null,
        preferred_contact_method: form.preferred_contact_method || null,
        message: form.message,
      });
      toast.success("Thanks — we'll get back to you shortly.");
      setForm({
        name: "",
        email: "",
        phone: buildPhoneFieldValue(),
        preferred_contact_method: null,
        message: "",
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-slate-950 text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid gap-12 md:grid-cols-2 items-start">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-300">Quick inquiry</p>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
            Tell us what you need.
          </h2>
          <p className="mt-3 max-w-md text-slate-300">
            Drop a few details and we'll come back with the best option — usually within a few hours.
          </p>
          <div className="mt-8 space-y-3 text-slate-300 text-sm">
            <p>✓ Short and long-term rentals</p>
            <p>✓ Airport pickups and drop-offs</p>
            <p>✓ Group day tours and custom itineraries</p>
          </div>
        </div>
        <form
          onSubmit={submit}
          data-testid="home-inquiry-form"
          className="rounded-xl bg-white/5 border border-white/10 p-6 space-y-4 backdrop-blur-sm"
        >
          <Field label="Your name" required>
            <input
              required
              data-testid="home-inquiry-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-10 w-full rounded-md bg-white/5 border border-white/10 px-3 text-white placeholder:text-slate-400 focus:border-sky-400 focus:outline-none"
              placeholder="Jane Doe"
            />
          </Field>
          <Field label="Email" required>
            <input
              required
              type="email"
              data-testid="home-inquiry-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-10 w-full rounded-md bg-white/5 border border-white/10 px-3 text-white placeholder:text-slate-400 focus:border-sky-400 focus:outline-none"
              placeholder="you@email.com"
            />
          </Field>
          <Field label="Phone / WhatsApp">
            <PhoneField
              value={form.phone}
              onChange={(phone) => setForm({ ...form, phone })}
              theme="dark"
              countryTestId="home-inquiry-phone-country"
              numberTestId="home-inquiry-phone"
              errorTestId="home-inquiry-phone-error"
            />
          </Field>
          <Field label="Preferred contact method">
            <PreferredContactMethodField
              value={form.preferred_contact_method}
              onChange={(preferred_contact_method) => setForm({ ...form, preferred_contact_method })}
              testid="home-inquiry-contact-method"
              theme="dark"
            />
          </Field>
          <Field label="What do you need?">
            <textarea
              rows={4}
              data-testid="home-inquiry-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2.5 text-white placeholder:text-slate-400 focus:border-sky-400 focus:outline-none"
              placeholder="Dates, pickup location, group size…"
            />
          </Field>
          <button
            type="submit"
            disabled={busy}
            data-testid="home-inquiry-submit"
            className="w-full rounded-md bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-sky-50 transition-colors disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send inquiry"}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">
        {label} {required && <span className="text-sky-300">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
